import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

/**
 * Theme = brand palette (currently only "classic"). Mode = light/dark comfort axis.
 * The two are orthogonal: a future "heritage" palette can still be light or dark.
 *
 * To add a new theme:
 *   1. Append a row to THEMES below.
 *   2. Add matching `.theme-<id>` (light) and `.theme-<id>.dark` blocks in src/styles.css.
 *   3. Optionally re-expose a picker in the UI — no component changes required here.
 */

export type ThemeId =
  | "classic"
  | "oxford-blue"
  | "cambridge-blue"
  | "college-funky"
  | "gcse-fast"
  | "sepia"
  | "ivy-league"
  | "nhs"
  | "journal"
  | "dawn";
export type Mode = "light" | "dark";

export const THEMES: { id: ThemeId; label: string }[] = [
  { id: "classic", label: "Classic" },
  { id: "journal", label: "Journal" },
  { id: "dawn", label: "Dawn" },
];


const MODE_KEY = "tigerseye-mode";
const THEME_KEY = "tigerseye-theme";

type Ctx = {
  theme: ThemeId;
  setTheme: (t: ThemeId) => void;
  mode: Mode;
  setMode: (m: Mode) => void;
  toggleMode: () => void;
};

const ThemeCtx = createContext<Ctx>({
  theme: "classic",
  setTheme: () => {},
  mode: "light",
  setMode: () => {},
  toggleMode: () => {},
});

function detectInitialMode(): Mode {
  if (typeof window === "undefined") return "light";
  try {
    const saved = localStorage.getItem(MODE_KEY) as Mode | null;
    if (saved === "light" || saved === "dark") return saved;
  } catch {}
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemeId>("classic");
  const [mode, setModeState] = useState<Mode>("light");

  useEffect(() => {
    try {
      const savedTheme = localStorage.getItem(THEME_KEY) as ThemeId | null;
      if (savedTheme && THEMES.some((t) => t.id === savedTheme)) setThemeState(savedTheme);
    } catch {}
    setModeState(detectInitialMode());
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    // Snapshot first — removing while iterating a live DOMTokenList skips entries.
    const stale = Array.from(root.classList).filter((cls) => cls.startsWith("theme-"));
    stale.forEach((cls) => root.classList.remove(cls));
    root.classList.add(`theme-${theme}`);
    root.setAttribute("data-theme", theme);
    try { localStorage.setItem(THEME_KEY, theme); } catch {}
  }, [theme]);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("dark", mode === "dark");
    root.style.colorScheme = mode;
    try { localStorage.setItem(MODE_KEY, mode); } catch {}
  }, [mode]);

  return (
    <ThemeCtx.Provider
      value={{
        theme,
        setTheme: setThemeState,
        mode,
        setMode: setModeState,
        toggleMode: () => setModeState((m) => (m === "dark" ? "light" : "dark")),
      }}
    >
      {children}
    </ThemeCtx.Provider>
  );
}

export const useTheme = () => useContext(ThemeCtx);
